'use client';

import axios from 'axios';

export interface AuthUser {
  _id: string;
  email: string;
  name?: string;
  role?: string;
  authProvider?: string;
  isVerified?: boolean;
  remember_me?: boolean;
}

export interface AuthResponse {
  token: string;
  user: AuthUser;
}

export interface AuthProviders {
  google: boolean;
  facebook: boolean;
  apple: boolean;
}

const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL || 'https://api.razee.app';

const authApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
});

const sanitizeUser = (user: Record<string, unknown>): AuthUser => ({
  _id: String(user._id || user.id || ''),
  email: String(user.email || ''),
  name: typeof user.name === 'string' ? user.name : undefined,
  role: typeof user.role === 'string' ? user.role : undefined,
  authProvider: typeof user.authProvider === 'string' ? user.authProvider : undefined,
  isVerified: typeof user.isVerified === 'boolean' ? user.isVerified : undefined,
  remember_me: typeof user.remember_me === 'boolean' ? user.remember_me : undefined,
});

const parseAuthResponse = (payload: Record<string, unknown>): AuthResponse => ({
  token: String(payload.token || ''),
  user: sanitizeUser((payload.user as Record<string, unknown>) || {}),
});

const parseAuthProviders = (payload: Record<string, unknown>): AuthProviders => ({
  google: Boolean(payload.google),
  facebook: Boolean(payload.facebook),
  apple: Boolean(payload.apple),
});

export async function registerUser(payload: {
  name?: string;
  email: string;
  password: string;
}): Promise<AuthResponse> {
  const response = await authApi.post('/api/auth/signup', payload);
  return parseAuthResponse(response.data);
}

export async function loginUser(payload: {
  email: string;
  password: string;
}): Promise<AuthResponse> {
  const response = await authApi.post('/api/auth/signin', payload);
  return parseAuthResponse(response.data);
}

export async function oauthSignIn(payload: {
  token: string;
  provider: 'google' | 'facebook' | 'apple';
}): Promise<AuthResponse> {
  const response = await authApi.post('/api/auth/oauth', payload);
  return parseAuthResponse(response.data);
}

export async function fetchAuthProviders(): Promise<AuthProviders> {
  const response = await authApi.get('/api/auth/providers');
  return parseAuthProviders((response.data as Record<string, unknown>) || {});
}

export async function fetchUserProfile(token: string): Promise<AuthUser> {
  const response = await authApi.get('/api/user/profile', {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return sanitizeUser(response.data);
}

export async function linkAnonymousUserData(token: string, anonymousUserId: string) {
  return authApi.post(
    '/api/user/link-anonymous',
    { anonymousUserId },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
}
